/**
 * Symphony GROUP CHAT, Phase 3b — the hooks that keep a group chat live on web: the realtime channel, the member's
 * read marker, leaving a group the viewer lost, and bringing an invitee back to their invite after sign-in and
 * onboarding (T36). All but the invite resume are behind `symphonyGroupChat`.
 */
import { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { useBasePath } from './use-base-path';
import { subscribeToChannel, type DuplexerEventName, type DuplexerPayload } from './duplexer-connection';
import { useGroupChatEnabled } from './group-chat-flag';
import { appendLiveGroupMessage, cachedGroupSummary, groupChatAction, groupMessageFromWire, handleLostAccess, liveRowEndsGroup, lostStateFromLiveRow, markGroupEnded, markLostGroup, onGroupConversationGone, replaceGroupMessage, useLastLiveGroupRow } from './group-chat-api';
import { patchChatsConversation, scheduleChatsInvalidation } from './chats';
import { queryClient, queryKeys } from './query-client';
import { useOnboardingEntry } from './use-onboarding-entry';
import { viewerRole, type GroupSummary, type GroupViewerState } from './group-chat';
import { deliveredAckTarget, groupEndedTarget, membersRefreshTarget, newestReadThroughId, realtimeMessageWire } from './group-realtime';
import { parsePresenceFrame, parseReceiptFrame, parseTypingFrame, PRESENCE_HEARTBEAT_MS, TypingSender } from './group-receipts';
import { applyGroupPresence, applyGroupTypingFrame, applyReceiptFrame, dropGroupTyping, noteGroupDelivered, sendGroupPresence, sendGroupTyping } from './group-live-state';
import type { Message } from './use-chat';
import { clearPendingGroupInvite, pendingGroupResumeHref } from './pending-group-invite';

export const GROUP_CHAT_CHANNEL = 'individuals-group-chat';
const CREATED: DuplexerEventName[] = ['created'];
/** Phase 10b: a row changed in place — a reaction, an edit, a pin, a delete for everyone. */
const UPDATED: DuplexerEventName[] = ['updated'];
/** VMR #34789: the group ENDED — sent to every member the end removed and the owner. */
const ENDED: DuplexerEventName[] = ['ended'];
/** Design §21 (2026-11-01): a person's delivered / read position, "is typing…", and online / last seen. */
const RECEIPT: DuplexerEventName[] = ['receipt'];
const TYPING: DuplexerEventName[] = ['typing'];
const PRESENCE: DuplexerEventName[] = ['presence'];

/** The viewer's own participant id in a group, from the `members` read or the chats list this browser holds. */
function viewerIdIn(conversationId: string): string | null {
  const known = queryClient.getQueryData<{ summary?: GroupSummary | null }>(['group-members', conversationId])?.summary ?? cachedGroupSummary(conversationId);
  return known?.viewerId ?? null;
}

/**
 * The member-scoped realtime channel: `created` carries `{entityId: conversationId, entity: <the ConversationMessage>}`
 * on the viewer's own metasite (`realtimeMessageWire` reads that, and the older wrapped `entity.conversationMessage`). A message for any group lands in the live rows (the open chat folds it in, deduped
 * by id — a post's clientMessageId is its id), and every chat list refreshes that row's preview and unread.
 */
export function useGroupChatRealtime(): void {
  const enabled = useGroupChatEnabled();
  useEffect(() => {
    // Flag off: no subscription at all. HTTPS only, like every channel (local dev runs --experimental-https).
    if (!enabled || typeof window === 'undefined' || window.location.protocol !== 'https:') return;
    return subscribeToChannel(GROUP_CHAT_CHANNEL, CREATED, (raw) => {
      const payload = raw as DuplexerPayload<unknown>;
      const conversationId = payload.entityId;
      const message = groupMessageFromWire(realtimeMessageWire(payload.entity));
      const row = message && (conversationId || message.conversationId) ? { ...message, conversationId: message.conversationId ?? conversationId } : null;
      if (row) {
        // Phase 10a: a message deleted for everyone arrives as its tombstone and replaces the row in place.
        if (row.groupMeta?.deleted) replaceGroupMessage(row);
        else appendLiveGroupMessage(row);
        // §21: its author has stopped typing (a Maestro's reply ends its "thinking"), and a message from someone else
        // is acked as delivered to this device — any group, open or not (coalesced per conversation).
        if (row.conversationId) {
          dropGroupTyping(row.conversationId, row.author);
          const ack = deliveredAckTarget(row, viewerIdIn(row.conversationId));
          if (ack) noteGroupDelivered(row.conversationId, ack.messageId, ack.createdAt);
        }
      }
      // The group ended (by anyone, the viewer included): it stays a group, with no composer (`markGroupEnded`).
      if (row?.conversationId && liveRowEndsGroup(row)) {
        markGroupEnded(row.conversationId);
      } else {
        // Phase 10a: a name / description / photo / settings line — the header, info screen and composer gate re-read.
        // A membership line (joined, left, a Maestro added, MEMBERS_ADDED) re-reads the participants too, so the `@`
        // picker and the info screen gain the newcomer while the chat is open — and so does a frame that is not a
        // message at all (a change published without a row).
        // A row from someone the held participants do not name re-reads them as well (`authorUnknownTo`).
        const rowGroup = row?.conversationId ?? conversationId;
        const known = rowGroup ? (queryClient.getQueryData<{ summary?: GroupSummary | null }>(['group-members', rowGroup])?.summary ?? cachedGroupSummary(rowGroup)) : null;
        const target = membersRefreshTarget(conversationId, row, known);
        // A MEMBER_REMOVED about this viewer: the open chat shows the state now.
        const lost = row && target ? lostStateFromLiveRow(target, row) : null;
        if (lost && target) {
          markLostGroup(target, lost);
        } else if (target) {
          void queryClient.invalidateQueries({ queryKey: ['group-members', target] });
          void queryClient.invalidateQueries({ queryKey: queryKeys.conversation(target) });
        }
      }
      scheduleChatsInvalidation();
      void queryClient.invalidateQueries({ queryKey: queryKeys.conversations });
    });
  }, [enabled]);
  useEffect(() => {
    if (!enabled || typeof window === 'undefined' || window.location.protocol !== 'https:') return;
    // Phase 10b: `updated` replaces the row of the same id IN PLACE (content and metadata — reactions, edited,
    // pinned, forwarded, deleted) and moves the pin banner; it never appends, and an id the open chat has not
    // loaded draws nothing (`applyGroupReplacements` only swaps rows it holds).
    return subscribeToChannel(GROUP_CHAT_CHANNEL, UPDATED, (raw) => {
      const payload = raw as DuplexerPayload<unknown>;
      const message = groupMessageFromWire(realtimeMessageWire(payload.entity));
      const conversationId = message?.conversationId ?? payload.entityId;
      if (!message || !conversationId) return;
      replaceGroupMessage({ ...message, conversationId });
      scheduleChatsInvalidation();
    });
  }, [enabled]);
  useEffect(() => {
    if (!enabled || typeof window === 'undefined' || window.location.protocol !== 'https:') return;
    // The group ENDED (VMR #34789): the open chat goes read-only in place (name and history kept, "This group has ended"
    // where the composer was), the GROUP_ENDED line lands (deduped against the `created` frame carrying the same row),
    // and every chat list re-reads so the row takes its ended form.
    return subscribeToChannel(GROUP_CHAT_CHANNEL, ENDED, (raw) => {
      const payload = raw as DuplexerPayload<unknown>;
      const target = groupEndedTarget(payload.entityId, payload.entity);
      if (!target) return;
      markGroupEnded(target.conversationId);
      const line = groupMessageFromWire(target.message);
      if (line) appendLiveGroupMessage({ ...line, conversationId: line.conversationId ?? target.conversationId });
      scheduleChatsInvalidation();
      void queryClient.invalidateQueries({ queryKey: queryKeys.conversations });
    });
  }, [enabled]);
}

/**
 * Design §21: the `receipt`, `typing` and `presence` frames on the member channel. Receipts move the open chat's ticks
 * and "Seen by"; typing draws in the header, the chat list row and the bottom of the chat; presence the online / last
 * seen lines. Frames about the viewer themselves are ignored. Behind `symphonyGroupChat`, like every frame here.
 */
export function useGroupLiveFrames(): void {
  const enabled = useGroupChatEnabled();
  useEffect(() => {
    if (!enabled || typeof window === 'undefined' || window.location.protocol !== 'https:') return;
    const offReceipt = subscribeToChannel(GROUP_CHAT_CHANNEL, RECEIPT, (raw) => {
      const payload = raw as DuplexerPayload<unknown>;
      const frame = parseReceiptFrame(payload.entityId, payload.entity);
      // An unknown person (a newcomer the held window predates) is ignored: the next window read lists them.
      if (frame && frame.metaSiteId !== viewerIdIn(frame.conversationId)) applyReceiptFrame(frame);
    });
    const offTyping = subscribeToChannel(GROUP_CHAT_CHANNEL, TYPING, (raw) => {
      const payload = raw as DuplexerPayload<unknown>;
      const frame = parseTypingFrame(payload.entityId, payload.entity);
      if (frame && frame.metaSiteId !== viewerIdIn(frame.conversationId)) applyGroupTypingFrame(frame);
    });
    const offPresence = subscribeToChannel(GROUP_CHAT_CHANNEL, PRESENCE, (raw) => {
      const payload = raw as DuplexerPayload<unknown>;
      const frame = parsePresenceFrame(payload.entityId, payload.entity);
      // The viewer's own never draws (every reader skips the viewer), so it needs no filter here.
      if (frame) applyGroupPresence(frame.metaSiteId, frame.presence);
    });
    return () => {
      offReceipt();
      offTyping();
      offPresence();
    };
  }, [enabled]);
}

/**
 * Design §21: the presence heartbeat while group chat is on — FOREGROUND when the page opens or comes back to the
 * front and every 30 s while it is in front; BACKGROUND (with `keepalive`) when it is hidden or closed. The timer
 * stops in the background. A hint: answers and failures are ignored.
 */
export function useGroupPresenceHeartbeat(): void {
  const enabled = useGroupChatEnabled();
  useEffect(() => {
    if (!enabled || typeof document === 'undefined') return;
    let timer: ReturnType<typeof setInterval> | null = null;
    let front = false;
    const foreground = () => {
      if (front) return;
      front = true;
      sendGroupPresence('FOREGROUND');
      timer = setInterval(() => sendGroupPresence('FOREGROUND'), PRESENCE_HEARTBEAT_MS);
    };
    const background = () => {
      if (timer) clearInterval(timer);
      timer = null;
      if (!front) return;
      front = false;
      sendGroupPresence('BACKGROUND');
    };
    const onVisibility = () => (document.visibilityState === 'visible' ? foreground() : background());
    if (document.visibilityState === 'visible') foreground();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', background);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', background);
      background();
    };
  }, [enabled]);
}

/**
 * Design §21: "is typing…" from the composer of an open group (`conversationId` null for anything else, an ended
 * group, or the flag off): `typing: true` at most every 3 s while the text is non-empty and changing, `false` after
 * 5 s idle, on send / clearing the text, on leaving the chat and when the page goes to the background.
 */
export function useGroupTypingSender(conversationId: string | null, text: string): void {
  const sender = useRef<{ id: string; sender: TypingSender } | null>(null);
  const lastText = useRef<string | null>(null);
  useEffect(() => {
    if (!conversationId) return undefined;
    const typing = new TypingSender((on) => sendGroupTyping(conversationId, on));
    sender.current = { id: conversationId, sender: typing };
    lastText.current = null;
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') typing.stop();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      typing.stop();
      if (sender.current?.sender === typing) sender.current = null;
    };
  }, [conversationId]);
  useEffect(() => {
    const current = sender.current;
    if (!current || current.id !== conversationId) return;
    // The first text seen in a chat (a restored draft) is not typing; a change after it is.
    const previous = lastText.current;
    lastText.current = text;
    if (previous === null) return;
    if (previous !== text) current.sender.onText(text);
  }, [conversationId, text]);
}

/** The newest persisted message id in a transcript (a pending post has no server id yet; a system ENDED line neither). */
export function newestMessageId(messages: Message[]): string | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (m.groupPost || m.id?.startsWith('group-ended-') || m.id?.startsWith('optimistic-')) continue;
    const id = m.finalId ?? m.id;
    if (id) return id;
  }
  return null;
}

/**
 * Opening a JOINED group marks it read with `viewer-state {readThroughMessageId}` — once per newest message, and
 * only while the reader can see the latest row. The owner's own row has no `viewerState` and keeps its acknowledge.
 */
export function useGroupReadThrough(conversationId: string | null, viewerState: GroupViewerState | null, messages: Message[], active: boolean): void {
  const sent = useRef<string | null>(null);
  // §21: a message that arrived LIVE while the chat is open is read now — it is not in the loaded window until the
  // next re-read, and the sender's ticks turn blue only once it is acked.
  const lastLive = useLastLiveGroupRow(viewerState ? conversationId : null);
  const newest = newestReadThroughId(messages, newestMessageId(messages), lastLive);
  useEffect(() => {
    if (!conversationId || !viewerState || !active || !newest) return;
    if (viewerState.lastReadMessageId === newest && !viewerState.hasUnread) return;
    const key = `${conversationId}:${newest}`;
    if (sent.current === key) return;
    sent.current = key;
    void groupChatAction('viewer-state', { conversationId, readThroughMessageId: newest }).then((outcome) => {
      if (outcome.kind === 'refused') {
        handleLostAccess(conversationId, outcome.refusal);
        return;
      }
      if (outcome.kind === 'ok' && outcome.data.viewerState) {
        patchChatsConversation(conversationId, { viewerState: outcome.data.viewerState });
      }
    });
  }, [conversationId, viewerState, active, newest]);
}

/**
 * Design §21, the OWNER's half of "read while open": the owner acknowledges through the window's read boundary
 * (`useChatReadAcknowledgment`), which only a window re-read moves — and a group message from someone else does not
 * re-read it (it arrives on the group channel, not the assistant's). So a live row from someone else, while the open
 * chat is in front, re-reads the window (debounced): the boundary moves, the acknowledge follows and the sender's
 * ticks turn blue now rather than on the next 30 s poll. The re-read also brings fresh receipts. Members are handled
 * by `useGroupReadThrough` reading the live row directly.
 */
export function useGroupOwnerLiveRead(conversationId: string | null, summary: GroupSummary | null, viewerState: GroupViewerState | null, active: boolean, reload: (() => unknown) | undefined): void {
  const lastLive = useLastLiveGroupRow(summary && !viewerState && !summary.ended ? conversationId : null);
  const seen = useRef<{ conversationId: string | null; id: string | null }>({ conversationId: null, id: null });
  const reloadRef = useRef(reload);
  reloadRef.current = reload;
  const viewerId = summary?.viewerId ?? null;
  const target = lastLive ? deliveredAckTarget(lastLive, viewerId) : null;
  const liveId = target?.messageId ?? null;
  useEffect(() => {
    const previous = seen.current;
    seen.current = { conversationId, id: liveId };
    // The live row already held when the chat opened is in the window that open reads.
    if (previous.conversationId !== conversationId || !liveId || previous.id === liveId || !active) return undefined;
    const timer = setTimeout(() => void reloadRef.current?.(), 800);
    return () => clearTimeout(timer);
  }, [conversationId, liveId, active]);
}

/**
 * Leave the open chat when its group is dropped (left, removed, ended), and treat a MEMBER's window read that 404s
 * as that same loss — the owner's 404 is not (their own chat stays theirs).
 */
export function useLeaveLostGroup(
  conversationId: string | null,
  summary: GroupSummary | null,
  viewerState: GroupViewerState | null,
  error: string | null,
  leave: () => void,
): void {
  const leaveRef = useRef(leave);
  leaveRef.current = leave;
  useEffect(() => onGroupConversationGone((gone) => {
    if (gone === conversationId) leaveRef.current();
  }), [conversationId]);
  // An ENDED group keeps what it shows: a re-read it refuses is not a removal.
  const isMember = !!summary && !summary.ended && (viewerState !== null || viewerRole(summary) !== 'OWNER');
  useEffect(() => {
    if (!conversationId || !isMember || !error) return;
    if (/\(404\)/.test(error)) handleLostAccess(conversationId, 'NOT_A_MEMBER');
  }, [conversationId, isMember, error]);
}

/**
 * T36 + Phase 10a: an invitee who had to sign in or sign up first lands back on their group invite
 * (`/group-invite/<id>`) or join link (`/group-join/<token>`). The invite and join pages park the item before anything
 * can bounce the reader away; once the account exists and the reader is anywhere else, go back to it (2026-11-02: no
 * longer after the whole onboarding chat).
 * The decision is `pendingGroupResumeHref`. Each target is replaced to once per mount (a parked invite and a parked
 * join link both get their turn). NOT behind the flag — neither page is.
 */
export function usePendingGroupResume(): void {
  const router = useRouter();
  const basePath = useBasePath();
  const entry = useOnboardingEntry();
  const last = useRef<string | null>(null);
  useEffect(() => {
    if (!router.isReady) return;
    // As soon as the account exists, not after onboarding (pendingGroupResumeHref): the invitee came for the group.
    const href = pendingGroupResumeHref(router.pathname, entry.resolved);
    if (!href || href === last.current) return;
    last.current = href;
    void router.replace(href, `${basePath}${href}`);
  }, [router, basePath, entry.resolved]);
}

export { clearPendingGroupInvite };
